
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LogEntry } from '@/types/workflow-replay';
import { getLogLevelColor } from '@/types/workflow-replay';

interface LogViewerProps {
    logs: LogEntry[];
    className?: string;
    maxHeight?: string;
}

/**
 * Log viewer component with level filtering and search.
 */
export function LogViewer({ logs, className, maxHeight = '400px' }: LogViewerProps) {
    const [search, setSearch] = useState('');
    const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
    const [levelFilter, setLevelFilter] = useState<string | null>(null);

    // Filter logs based on search and level
    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            // Level filter
            if (levelFilter && log.level.toLowerCase() !== levelFilter.toLowerCase()) {
                return false;
            }
            // Search filter
            if (search) {
                const searchLower = search.toLowerCase();
                return (
                    log.message.toLowerCase().includes(searchLower) ||
                    JSON.stringify(log.attributes).toLowerCase().includes(searchLower)
                );
            }
            return true;
        });
    }, [logs, search, levelFilter]);

    // Count logs by level
    const levelCounts = useMemo(() => {
        const counts: Record<string, number> = { error: 0, warning: 0, info: 0, debug: 0 };
        for (const log of logs) {
            const level = log.level.toLowerCase();
            if (level in counts) {
                counts[level]++;
            }
        }
        return counts;
    }, [logs]);

    const toggleExpanded = (index: number) => {
        setExpandedLogs(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    if (logs.length === 0) {
        return (
            <div className={cn('text-sm text-slate-500 italic py-4 text-center', className)}>
                No logs available
            </div>
        );
    }

    return (
        <div className={cn('space-y-2', className)}>
            {/* Filters */}
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search logs..."
                        className="h-8 pl-8 text-sm"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <div className="flex gap-1">
                    {(['error', 'warning', 'info', 'debug'] as const).map(level => (
                        <Button
                            key={level}
                            variant={levelFilter === level ? 'default' : 'outline'}
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={() => setLevelFilter(levelFilter === level ? null : level)}
                            disabled={levelCounts[level] === 0}
                        >
                            {level}
                            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                                {levelCounts[level]}
                            </Badge>
                        </Button>
                    ))}
                </div>
            </div>

            {/* Log entries */}
            <div 
                className="overflow-auto rounded-md border border-slate-200 bg-white"
                style={{ maxHeight }}
            >
                {filteredLogs.length === 0 ? (
                    <div className="py-4 text-center text-sm text-slate-500">
                        No matching logs
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {filteredLogs.map((log, index) => {
                            const isExpanded = expandedLogs.has(index);
                            const hasAttributes = Object.keys(log.attributes || {}).length > 0;

                            return (
                                <div key={index} className="text-xs">
                                    <div
                                        className={cn(
                                            'flex items-start gap-2 p-2',
                                            hasAttributes && 'cursor-pointer hover:bg-slate-50'
                                        )}
                                        onClick={() => hasAttributes && toggleExpanded(index)}
                                    >
                                        {hasAttributes && (
                                            <span className="mt-0.5 text-slate-400">
                                                {isExpanded ? (
                                                    <ChevronDown className="h-3 w-3" />
                                                ) : (
                                                    <ChevronRight className="h-3 w-3" />
                                                )}
                                            </span>
                                        )}
                                        <span className="flex-shrink-0 text-slate-400 font-mono">
                                            {formatTimestamp(log.timestamp)}
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className={cn(
                                                'flex-shrink-0 h-4 px-1.5 text-[10px] font-medium uppercase',
                                                getLogLevelColor(log.level)
                                            )}
                                        >
                                            {log.level}
                                        </Badge>
                                        <span className="flex-1 break-all text-slate-700">
                                            {log.message}
                                        </span>
                                    </div>
                                    {isExpanded && hasAttributes && (
                                        <div className="bg-slate-50 px-6 py-2 font-mono text-[11px]">
                                            <pre className="whitespace-pre-wrap text-slate-600">
                                                {JSON.stringify(log.attributes, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Summary */}
            <div className="text-xs text-slate-500">
                Showing {filteredLogs.length} of {logs.length} log entries
            </div>
        </div>
    );
}

/**
 * Format ISO timestamp to a shorter display format.
 */
function formatTimestamp(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }) + '.' + String(date.getMilliseconds()).padStart(3, '0');
    } catch {
        return timestamp;
    }
}

